import { useEffect, useState } from "react";
import { api, type JobView } from "../api";
import { navigate, routes } from "../router";
import { OverviewTab } from "../components/OverviewTab";
import { TranscriptTab } from "../components/TranscriptTab";
import { FramesTab } from "../components/FramesTab";
import { TimelineTab } from "../components/TimelineTab";
import { SpecTab } from "../components/SpecTab";
import { FlowTab } from "../components/FlowTab";
import {
  EvidenceDownloadButton,
  type BundleStatus,
} from "../components/EvidenceDownloadButton";

interface Props {
  jobId: string;
  tab: string;
  onAnother: () => void;
}

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "transcript", label: "Transcript" },
  { key: "frames", label: "Frames" },
  { key: "timeline", label: "Timeline" },
  { key: "spec", label: "Spec" },
  { key: "flow", label: "Flow" },
] as const;

const TERMINAL = new Set([
  "succeeded",
  "succeededwithwarnings",
  "failed",
  "cancelled",
]);
const RETENTION_DAYS = 7;
const POLL_TIMEOUT_MS = 60 * 60 * 1000;

export function Job({ jobId, tab, onAnother }: Props) {
  const [job, setJob] = useState<JobView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pollExpired, setPollExpired] = useState(false);
  const [bundleStatus, setBundleStatus] = useState<BundleStatus>({ kind: "probing" });
  const startedAt = useState(() => Date.now())[0];

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function tick() {
      try {
        const j = await api.getJob(jobId);
        if (cancelled) return;
        setJob(j);
        if (TERMINAL.has(j.status.toLowerCase())) return;
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          setPollExpired(true);
          return;
        }
        timer = setTimeout(tick, 3000);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    }
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, startedAt]);

  // Probe bundle availability once the job reaches a terminal state. Until
  // then there's nothing to download and we hold "probing".
  useEffect(() => {
    if (!job) return;
    if (!TERMINAL.has(job.status.toLowerCase())) return;
    let cancelled = false;
    setBundleStatus({ kind: "probing" });
    api.probeEvidence(jobId).then((r) => {
      if (cancelled) return;
      if (r.status === "available") setBundleStatus({ kind: "available" });
      else if (r.status === "expired")
        setBundleStatus({ kind: "expired", expiredAt: r.expiredAt });
      else setBundleStatus({ kind: "unavailable" });
    });
    return () => {
      cancelled = true;
    };
    // re-probe when terminal status flips
  }, [jobId, job?.status]);

  if (error) {
    return (
      <div className="card">
        <div className="error">{error}</div>
        <p style={{ marginTop: 16 }}>
          <button className="secondary" onClick={onAnother}>
            Submit another video
          </button>
        </p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="card">
        <p className="muted">Loading job {jobId}…</p>
      </div>
    );
  }

  const status = job.status.toLowerCase();
  const isTerminal = TERMINAL.has(status);
  const expired = isExpired(job);
  const succeeded = status === "succeeded" || status === "succeededwithwarnings";

  return (
    <div className="card">
      <header className="row" style={{ marginBottom: 16 }}>
        <div>
          <button
            className="secondary"
            onClick={() => navigate(routes.history())}
            style={{ marginBottom: 8 }}
          >
            ← Back to jobs
          </button>
          <h2 style={{ margin: 0 }}>
            Job <code style={{ fontSize: 14 }}>{jobId.slice(0, 12)}…</code>
          </h2>
        </div>
        {succeeded && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {expired ? (
              <button
                className="primary"
                disabled
                title="Artifacts have been auto-deleted (7-day retention). Re-submit the source video to regenerate."
              >
                change.zip
              </button>
            ) : (
              <a href={api.downloadUrl(jobId)}>
                <button className="primary">change.zip</button>
              </a>
            )}
            <EvidenceDownloadButton jobId={jobId} status={bundleStatus} compact />
          </div>
        )}
      </header>

      <nav className="tab-bar" role="tablist">
        {TABS.map((t) => {
          const enabled = t.key === "overview" || isTerminal;
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              className={`tab ${active ? "tab-active" : ""}`}
              disabled={!enabled}
              title={enabled ? "" : "Available when the job completes"}
              onClick={() => navigate(routes.job(jobId, t.key))}
            >
              {t.label}
              {expired && t.key !== "overview" && (
                <span className="badge badge-muted" style={{ marginLeft: 6 }}>
                  expired
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="tab-body">
        {tab === "overview" && (
          <>
            <OverviewTab job={job} expired={expired} bundleStatus={bundleStatus} />
            {pollExpired && (
              <div className="error" style={{ marginTop: 16 }}>
                Job is taking longer than expected — check back later. Share
                this URL: <code>{window.location.href}</code>
              </div>
            )}
          </>
        )}
        {!isTerminal && tab !== "overview" && (
          <p className="muted">
            Available when the job completes (we're still {job.status.toLowerCase()}).
          </p>
        )}
        {isTerminal && tab === "transcript" && <TranscriptTab jobId={jobId} />}
        {isTerminal && tab === "frames" && <FramesTab jobId={jobId} />}
        {isTerminal && tab === "timeline" && <TimelineTab jobId={jobId} />}
        {isTerminal && tab === "spec" && <SpecTab jobId={jobId} />}
        {isTerminal && tab === "flow" && <FlowTab jobId={jobId} />}
      </div>

      {isTerminal && (
        <p style={{ marginTop: 24 }}>
          <button className="secondary" onClick={onAnother}>
            Submit another video
          </button>
        </p>
      )}
    </div>
  );
}

function isExpired(j: JobView): boolean {
  const t = Date.parse(j.created_at);
  if (Number.isNaN(t)) return false;
  return Date.now() - t > RETENTION_DAYS * 24 * 60 * 60 * 1000;
}
